import { Award, Gift, Heart, ShoppingBag, ShoppingCart, Sparkles, Star } from 'lucide-react';
import { formatMoney } from '@/utils/currency';

export type LoyaltyCardDesign = 'Fresh' | 'Tech' | 'Style';

export type LoyaltyCardTier = {
  tier_name?: string;
  badge_color?: string;
  min_points?: number;
} | null;

export type LoyaltyMemberCardProps = {
  design?: LoyaltyCardDesign | string;
  colour?: string;
  logoUrl?: string | null;
  shopName?: string;
  tagline?: string;
  programName?: string;
  customerName?: string;
  memberId?: string;
  points?: number;
  value?: number;
  tier?: LoyaltyCardTier;
  qrDataUrl?: string | null;
  /** Shop design toggles — default on */
  showPoints?: boolean;
  showValue?: boolean;
  showTier?: boolean;
  /** Compact preview in settings */
  preview?: boolean;
  className?: string;
};

const DESIGN_DEFAULTS: Record<
  LoyaltyCardDesign,
  { colour: string; accent: string; icon: 'leaf' | 'bag' | 'hanger' }
> = {
  Fresh: { colour: '#15803d', accent: '#facc15', icon: 'leaf' },
  Tech: { colour: '#1d4ed8', accent: '#38bdf8', icon: 'bag' },
  Style: { colour: '#7e22ce', accent: '#f472b6', icon: 'hanger' },
};

function normalizeDesign(d?: string): LoyaltyCardDesign {
  if (d === 'Tech' || d === 'Style' || d === 'Fresh') return d;
  return 'Fresh';
}

function hexToRgb(hex: string) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return { r: 13, g: 148, b: 136 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function ProgramIcon({ design }: { design: LoyaltyCardDesign }) {
  if (design === 'Tech') return <Award className="w-3.5 h-3.5" />;
  if (design === 'Style') return <Heart className="w-3.5 h-3.5" />;
  return <Gift className="w-3.5 h-3.5" />;
}

function TierIcon({ name }: { name?: string }) {
  const n = (name || '').toLowerCase();
  if (n.includes('plat')) return <Sparkles className="w-5 h-5" />;
  if (n.includes('gold')) return <Award className="w-5 h-5" />;
  if (n.includes('silver') || n.includes('vip')) return <Star className="w-5 h-5" />;
  return <Award className="w-5 h-5" />;
}

/**
 * Printable / downloadable loyalty member card (Fresh / Tech / Style templates).
 * Points / value / tier columns respect shop design toggles; tier also needs a defined tier.
 */
export default function LoyaltyMemberCard({
  design: rawDesign,
  colour,
  logoUrl,
  shopName = 'Your Shop',
  tagline = '',
  programName = 'Loyalty Rewards',
  customerName = 'Member',
  memberId = '',
  points = 0,
  value,
  tier,
  qrDataUrl,
  showPoints = true,
  showValue = true,
  showTier: showTierPref = true,
  preview = false,
  className = '',
}: LoyaltyMemberCardProps) {
  const design = normalizeDesign(rawDesign);
  const defaults = DESIGN_DEFAULTS[design];
  const primary = colour || defaults.colour;
  const rgb = hexToRgb(primary);
  const showTierCol = showTierPref && Boolean(tier?.tier_name);
  const showPointsCol = showPoints;
  const showValueLine = showValue && value != null;
  const showStatsRow = showPointsCol || showTierCol;

  return (
    <div
      className={`relative overflow-hidden rounded-[1.75rem] bg-white shadow-xl border border-slate-100 ${
        preview ? 'w-full max-w-[280px]' : 'w-full max-w-[360px]'
      } ${className}`}
      style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}
    >
      {/* Header */}
      <div
        className="relative px-5 pt-5 pb-10 text-white"
        style={{
          background: `linear-gradient(145deg, ${primary} 0%, rgb(${Math.max(0, rgb.r - 40)}, ${Math.max(
            0,
            rgb.g - 30,
          )}, ${Math.max(0, rgb.b - 20)}) 100%)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-white/95 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="w-full h-full object-contain p-1" />
              ) : (
                <span className="text-sm font-bold" style={{ color: primary }}>
                  {(shopName || 'S').slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm leading-tight truncate">{shopName}</p>
              {tagline ? (
                <p className="text-[11px] text-white/80 truncate">{tagline}</p>
              ) : (
                <p className="text-[11px] text-white/70">Loyalty programme</p>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-white/90">
              <ProgramIcon design={design} />
              {programName || 'Loyalty Rewards'}
            </div>
          </div>
        </div>

        {/* Wave */}
        <svg
          className="absolute left-0 right-0 -bottom-px w-full h-8"
          viewBox="0 0 360 32"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            d="M0 18 C60 32 120 4 180 16 C240 28 300 8 360 20 L360 32 L0 32 Z"
            fill="#ffffff"
          />
        </svg>
      </div>

      {/* Body */}
      <div className={`px-5 ${preview ? 'pb-4 pt-1' : 'pb-5 pt-1'} space-y-4`}>
        <div className="flex flex-col items-center">
          <div className="rounded-2xl bg-white p-2.5 shadow-md border border-slate-100">
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="Loyalty QR"
                className={preview ? 'w-28 h-28' : 'w-40 h-40'}
              />
            ) : (
              <div
                className={`${preview ? 'w-28 h-28' : 'w-40 h-40'} bg-slate-100 rounded-lg flex items-center justify-center text-xs text-slate-400`}
              >
                QR
              </div>
            )}
          </div>
          {memberId && (
            <p className="mt-2.5 text-[11px] text-slate-500 font-mono tracking-wide">
              Member ID: {memberId}
            </p>
          )}
          <p className="mt-1 text-base font-bold text-slate-900 capitalize flex items-center gap-1.5">
            <span
              className="inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] text-white"
              style={{ backgroundColor: primary }}
            >
              {(customerName || 'M').slice(0, 1).toUpperCase()}
            </span>
            {customerName}
          </p>
        </div>

        {showStatsRow && (
          <div
            className={`grid ${
              showPointsCol && showTierCol ? 'grid-cols-2' : 'grid-cols-1'
            } gap-3 pt-1 border-t border-slate-100`}
          >
            {showPointsCol && (
              <div className="text-center py-2">
                <div className="inline-flex items-center gap-1 text-[11px] text-slate-500 mb-1">
                  <Star className="w-3.5 h-3.5" style={{ color: primary }} />
                  Your Points
                </div>
                <p className="text-2xl font-bold tabular-nums" style={{ color: primary }}>
                  {Math.floor(points || 0)}
                </p>
                {showValueLine && (
                  <p className="text-[11px] text-slate-400 mt-0.5">{formatMoney(value)}</p>
                )}
              </div>
            )}
            {showTierCol && (
              <div
                className={`text-center py-2 ${
                  showPointsCol ? 'border-l border-slate-100' : ''
                }`}
              >
                <div className="inline-flex items-center gap-1 text-[11px] text-slate-500 mb-1">
                  <span style={{ color: tier?.badge_color || primary }}>
                    <TierIcon name={tier?.tier_name} />
                  </span>
                  Tier
                </div>
                <p
                  className="text-xl font-bold"
                  style={{ color: tier?.badge_color || primary }}
                >
                  {tier?.tier_name}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer perks */}
      <div
        className="px-4 py-3 grid grid-cols-3 gap-1 text-center text-white"
        style={{ backgroundColor: primary }}
      >
        <div className="space-y-1">
          <ShoppingCart className="w-4 h-4 mx-auto opacity-90" />
          <p className="text-[9px] leading-tight font-medium opacity-95">Earn on every purchase</p>
        </div>
        <div className="space-y-1">
          <Gift className="w-4 h-4 mx-auto opacity-90" />
          <p className="text-[9px] leading-tight font-medium opacity-95">Redeem for rewards</p>
        </div>
        <div className="space-y-1">
          <ShoppingBag className="w-4 h-4 mx-auto opacity-90" />
          <p className="text-[9px] leading-tight font-medium opacity-95">
            {showTierCol ? 'Member offers' : 'Exclusive deals'}
          </p>
        </div>
      </div>
    </div>
  );
}

export const LOYALTY_CARD_DESIGNS: {
  id: LoyaltyCardDesign;
  label: string;
  blurb: string;
  defaultColour: string;
}[] = [
  { id: 'Fresh', label: 'Fresh', blurb: 'Clean retail / grocery look', defaultColour: '#15803d' },
  { id: 'Tech', label: 'Tech', blurb: 'Modern electronics vibe', defaultColour: '#1d4ed8' },
  { id: 'Style', label: 'Style', blurb: 'Fashion & lifestyle', defaultColour: '#7e22ce' },
];
